import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useServerView, activeFilters, ListActions, ListPrint } from "@/components/list/ListView";
import { QrFilterBar } from "@/components/quality/QrFilterBar";
import { PSummary } from "@/components/print/PrintDoc";
import { pl } from "@/components/quality/QrTools";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, ChevronRight, ChevronDown, FolderKanban, Building2, Inbox, CircleDashed, Clock, CheckCircle2, Trash2, ArrowUp, ArrowDown, ArrowUpDown, Star, X } from "lucide-react";

const daDate = (s) => { const d = String(s || "").slice(0, 10); if (d.length === 10) { const [y, m, dd] = d.split("-"); return `${dd}-${m}-${y}`; } return "—"; };
const fmtHours = (h) => (h && h > 0) ? `${String(h).replace(".", ",")} t` : "—";
const TYPE_LABEL = { kvalitet: "Kvalitet", indkoeb: "Indkøb" };
const STATUS_ORDER = { "Ny": 0, "I gang": 1, "Afventer svar": 2, "Færdig": 3 };
const STATUS_COLOR = {
  "Ny": "bg-stone-50 text-stone-500 border-stone-200",
  "I gang": "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]",
  "Afventer svar": "bg-[#F9F1D9] text-[#8A6E17] border-[#ECE0B6]",
  "Færdig": "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]",
};
const STATUS_DOT = { "Ny": "#A8A29E", "I gang": "#A08979", "Afventer svar": "#C9A83F", "Færdig": "#7E856C" };
const PRIO_DOT = { "Høj": "bg-[#A83232]", "Normal": "bg-[#A08979]", "Lav": "bg-stone-300" };

const STATUS_OPTIONS = [
  { value: "Ny", label: "Ny" },
  { value: "I gang", label: "I gang" },
  { value: "Afventer svar", label: "Afventer svar" },
  { value: "Færdig", label: "Færdig" },
];
const KILDE_OPTIONS = [
  { value: "projekt", label: "Fra projekt" },
  { value: "ny", label: "Nye sager" },
];

const sorters = {
  title: (a, b) => (a.title || "").localeCompare(b.title || "", "da"),
  responsible: (a, b) => ((a.assigned_users || [])[0]?.name || "").localeCompare((b.assigned_users || [])[0]?.name || "", "da"),
  source: (a, b) => (a.source_project_id ? "0" + (a.source_project_name || "") : "1").localeCompare(b.source_project_id ? "0" + (b.source_project_name || "") : "1", "da"),
  status: (a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9),
  due: (a, b) => String(a.due_date || "9999-99-99").localeCompare(String(b.due_date || "9999-99-99")),
  hours: (a, b) => (a.total_hours || 0) - (b.total_hours || 0),
};

function Stat({ icon: Icon, label, value, accent, iconColor, onClick, active }) {
  return (
    <div className={`pcard pcard-hover p-5 flex items-center gap-4 cursor-pointer ${active ? "ring-2 ring-[#A83232] ring-offset-2 ring-offset-[#FBFAF8]" : ""}`}
      data-testid={`case-stat-${label}`} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}>
      <div className={`h-11 w-11 rounded-2xl flex items-center justify-center ${accent}`}><Icon className="h-5 w-5" strokeWidth={1.75} style={{ color: iconColor }} /></div>
      <div>
        <p className="text-2xl font-semibold tracking-tight leading-none text-[#1A1919]">{value}</p>
        <p className="label-caps mt-1.5">{label}</p>
      </div>
    </div>
  );
}

export default function Cases({ type = null, mine = false }) {
  const { user, updateUser } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const sv = useServerView(`/case-view/list?${mine ? "mine=true" : `type=${type}`}`, { exportPath: `/case-view/list/export.xlsx?${mine ? "mine=true" : `type=${type}`}` });
  const cases = sv.data === null ? null : sv.rows;
  const loadErr = false;
  const [caseToDelete, setCaseToDelete] = useState(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (id) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const uid = user?.id;
  const createTo = mine ? "/salg/send-sag" : `/${type === "kvalitet" ? "kvalitet" : "indkoeb"}/opret`;

  const reload = sv.reload;
  const delCase = async () => { try { await api.delete(`/cases/${caseToDelete.id}`); toast.success("Sag slettet"); setCaseToDelete(null); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Kunne ikke slette sag"); } };
  const exportExcel = async () => {
    try {
      const qp = new URLSearchParams(params);
      if (mine) qp.set("mine", "true"); else if (type) qp.set("type", type);
      const res = await api.get(`/cases/export/excel?${qp.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `sager-${mine ? "mine" : type}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Kunne ikke eksportere til Excel"); }
  };

  const loadSaved = () => api.get("/saved-filters?scope=cases").then((r) => setSavedFilters(r.data)).catch(() => {});
  useEffect(() => { loadSaved(); }, []);

  // ---- URL-baserede filtre (samme mønster som projektoversigten) ----
  const getArr = sv.getArr;
  const kundeSel = getArr("kunde");
  const respSel = getArr("ansvarlig");
  const statusSel = getArr("status");
  const kildeSel = getArr("kilde");
  const match = sv.match;
  const sort = params.get("sort") || "";
  const dir = params.get("dir") || "asc";
  const visning = params.get("visning");
  const setParam = sv.setParam;
  const resetFilters = sv.reset;
  const toggleSort = (col) => setParam(sort === col ? { sort: col, dir: dir === "asc" ? "desc" : "asc" } : { sort: col, dir: "asc" });
  const applySaved = (sf) => setParams(new URLSearchParams(sf.query || ""));
  const saveCurrent = async () => {
    if (!saveName.trim()) return;
    try { await api.post("/saved-filters", { name: saveName.trim(), query: params.toString(), scope: "cases" }); toast.success("Filter gemt"); setSaveOpen(false); setSaveName(""); loadSaved(); }
    catch (e) { toast.error("Kunne ikke gemme filter"); }
  };
  const deleteSaved = async (id, e) => { e?.stopPropagation?.(); try { await api.delete(`/saved-filters/${id}`); toast.success("Filter slettet"); loadSaved(); } catch (_) {} };

  // Status-KPI som hurtigfilter (nogle kort dækker flere statusser)
  const toggleStatusCard = (vals) => {
    const allOn = vals.every((v) => statusSel.includes(v));
    setParam({ status: allOn ? statusSel.filter((v) => !vals.includes(v)) : [...new Set([...statusSel, ...vals])] });
  };
  const cardActive = (vals) => vals.length > 0 && vals.every((v) => statusSel.includes(v));

  // Hurtig visning-standard (kun afdelings-oversigt, ikke "Mine sager")
  const currentView = visning === "mit-ansvar" ? "mit-ansvar" : "alle";
  const defaultView = user?.default_case_view || "alle";
  const appliedDefault = useRef(false);
  useEffect(() => {
    if (appliedDefault.current || !user || mine) return;
    appliedDefault.current = true;
    if (!params.get("visning") && user.default_case_view === "mit-ansvar") setParam({ visning: "mit-ansvar" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const saveDefault = async () => {
    try {
      const r = await api.patch("/auth/me/preferences", { default_case_view: currentView });
      updateUser({ default_case_view: r.data.default_case_view });
      toast.success(currentView === "mit-ansvar" ? "Standard sat til Mit ansvar" : "Standard sat til Alle sager");
    } catch (e) { toast.error("Kunne ikke gemme standardvisning"); }
  };

  const filtered = useMemo(() => (sorters[sort] ? [...(cases || [])].sort((a, b) => sorters[sort](a, b) * (dir === "asc" ? 1 : -1)) : (cases || [])), [cases, sort, dir]);
  const kpi = sv.data?.counts || { ny: 0, igang: 0, afventer: 0, loest: 0 };

  const anyFilter = [kundeSel, respSel, statusSel, kildeSel, getArr("fremdrift"), getArr("timer")].some((a) => a.length) || match === "eller" || (!mine && visning === "mit-ansvar") || !!sv.search || sv.period.from || sv.period.to;
  const total = sv.data?.total ?? 0;
  const pfilters = activeFilters(sv, { kunde: "Kunde", ansvarlig: "Sagsansvarlig", status: "Status", kilde: "Kilde", fremdrift: "Fremdrift", timer: "Timer" });
  if (!mine && visning === "mit-ansvar") pfilters.unshift(["Visning", "Mit ansvar"]);
  const title = mine ? "Mine sager" : TYPE_LABEL[type];

  const SortIcon = ({ col }) => sort !== col
    ? <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
    : dir === "asc" ? <ArrowUp className="h-3.5 w-3.5 text-primary" /> : <ArrowDown className="h-3.5 w-3.5 text-primary" />;
  const SortH = ({ col, span, align = "left", children }) => (
    <button type="button" onClick={() => toggleSort(col)} data-testid={`case-sort-${col}`} className={`${span} flex items-center gap-1.5 label-caps hover:text-stone-700 [transition-property:color] duration-200 ${align === "right" ? "justify-end" : ""}`}>{children} <SortIcon col={col} /></button>
  );

  return (
    <div className="rise-in" data-testid="cases-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-[#1A1919]">{mine ? "Mine sager" : TYPE_LABEL[type]}</h1>
          <p className="text-stone-500 mt-2">{mine ? "Sager du har sendt afsted — følg status og svar." : `Sager for ${TYPE_LABEL[type].toLowerCase()}safdelingen — fra projekter og nye sager.`}</p>
        </div>
        <div className="flex gap-2.5">
          <ListActions onExport={sv.exportXlsx} more={[]}
            primary={<Button onClick={() => nav(createTo)} className="rounded-xl font-medium bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="cases-create-btn">
            <Plus className="h-4 w-4 mr-2" /> {mine ? "Send sag" : "Opret ny sag"}
          </Button>} />
        </div>
      </div>

      {!mine && visning === "mit-ansvar" && (
        <div className="flex items-center gap-2 mb-6" data-testid="case-personal-banner">
          <span className="inline-flex items-center gap-2 bg-[#F1DEDA] text-[#A83232] border border-[#E9C9C3] rounded-full pl-3.5 pr-2 py-1.5 text-sm font-medium">
            Mit ansvar
            <button onClick={() => setParam({ visning: null })} data-testid="case-clear-personal" className="p-0.5 hover:bg-[#E9C9C3] rounded-full"><X className="h-3.5 w-3.5" /></button>
          </span>
          <span className="text-sm text-muted-foreground">{filtered.length} sager</span>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <h2 className="label-caps">Nøgletal</h2>
        {anyFilter && <span className="text-xs font-medium text-[#A83232] bg-[#F1DEDA] border border-[#E9C9C3] rounded-full px-2 py-0.5" data-testid="case-kpi-filtered">filtreret</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        <Stat icon={Inbox} label="Nye" value={kpi.ny} accent="bg-stone-100" iconColor="#A8A29E" onClick={() => toggleStatusCard(["Ny"])} active={cardActive(["Ny"])} />
        <Stat icon={Clock} label="I gang" value={kpi.igang} accent="bg-[#F4EFEC]" iconColor="#A08979" onClick={() => toggleStatusCard(["I gang"])} active={cardActive(["I gang"])} />
        <Stat icon={CircleDashed} label="Afventer svar" value={kpi.afventer} accent="bg-[#F9F1D9]" iconColor="#C9A83F" onClick={() => toggleStatusCard(["Afventer svar"])} active={cardActive(["Afventer svar"])} />
        <Stat icon={CheckCircle2} label="Færdig" value={kpi.loest} accent="bg-[#E7EADF]" iconColor="#7E856C" onClick={() => toggleStatusCard(["Færdig"])} active={cardActive(["Færdig"])} />
      </div>

      <QrFilterBar search={sv.search} setSearch={sv.onSearch} hideQuick placeholder="Søg sag, kunde, projekt, oprettet af…" shown={filtered.length} total={total} onReset={resetFilters} leadActive={!mine && visning === "mit-ansvar"} match={match} setMatch={sv.setMatch}
        filters={[sv.mkFilter("kunde", "Kunde"), sv.mkFilter("ansvarlig", "Sagsansvarlig"), sv.mkFilter("status", "Status"), sv.mkFilter("kilde", "Kilde"), sv.mkFilter("fremdrift", "Fremdrift"), sv.mkFilter("timer", "Timer")]} period={sv.period}
        saved={savedFilters} onApplySaved={applySaved} onDeleteSaved={deleteSaved} onSaveCurrent={() => setSaveOpen(true)}
        lead={!mine && <>
          <div className="inline-flex rounded-xl border border-[#EBE8E3] bg-white p-0.5 h-9 items-center" data-testid="case-quick-view-toggle">
            <button type="button" data-testid="case-quick-all" onClick={() => setParam({ visning: null })} className={`px-3.5 h-full rounded-[10px] text-sm font-medium [transition-property:background-color,color] duration-200 ${!visning ? "bg-[#A83232] text-white" : "text-stone-600 hover:text-stone-900"}`}>Alle sager</button>
            <button type="button" data-testid="case-quick-mit-ansvar" onClick={() => setParam({ visning: "mit-ansvar" })} className={`px-3.5 h-full rounded-[10px] text-sm font-medium [transition-property:background-color,color] duration-200 ${visning === "mit-ansvar" ? "bg-[#A83232] text-white" : "text-stone-600 hover:text-stone-900"}`}>Mit ansvar</button>
          </div>
          {defaultView === currentView
            ? <span className="inline-flex items-center gap-1 text-xs text-[#A83232] font-medium" data-testid="case-default-view-active"><Star className="h-3.5 w-3.5 fill-[#A83232]" /> Din standard</span>
            : <button type="button" onClick={saveDefault} data-testid="case-set-default-view" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-[#A83232] [transition-property:color] duration-200"><Star className="h-3.5 w-3.5" /> Sæt som standard</button>}
        </>} />
      <ListPrint kicker={`BSM A/S · ${mine ? "Salg" : TYPE_LABEL[type]}`} title={title} noun="sager" filters={pfilters}
        kpis={[{ label: "Sager i udvalget", value: filtered.length }, { label: "Nye", value: kpi.ny }, { label: "I gang", value: kpi.igang, dark: true }, { label: "Afventer svar", value: kpi.afventer }]}
        summary={<PSummary>{pl(filtered.length, "sag", "sager")} i udvalget ud af {total} i alt – {kpi.ny} nye, {kpi.igang} i gang, {kpi.afventer} afventer svar og {pl(kpi.loest, "færdig", "færdige")}.</PSummary>}
        cols={[{ label: "Sag", key: "t2" }, { label: "Sagsansvarlig", key: "r2", w: "32mm" }, { label: "Kilde", key: "src2", w: "34mm" }, { label: "Status", key: "status", w: "24mm", cls: "nowrap" }, { label: "Deadline", key: "dl", w: "24mm", cls: "nowrap" }, { label: "Timer", key: "hrs", w: "14mm", cls: "nowrap num" }]}
        rows={filtered.map((c) => ({ ...c, t2: <span><span className="pd-name">{c.title}</span><span className="pd-sub2">{c.customer_name || "—"}</span></span>, r2: (c.assigned_users || []).map((u) => u.name).join(", ") || "—", src2: c.source_project_id ? (c.source_project_name || "Projekt") : "Ny sag", dl: c.due_date ? daDate(c.due_date) : "Ingen", hrs: fmtHours(c.total_hours) }))} />

      {cases === null ? (
        <div className="pcard p-8 text-muted-foreground text-sm">Indlæser sager…</div>
      ) : loadErr ? (
        <div className="pcard p-8 text-center" data-testid="cases-error"><p className="text-[#A83232] text-sm">Kunne ikke hente sager. Prøv at genindlæse siden.</p></div>
      ) : total === 0 ? (
        <div className="pcard p-16 text-center" data-testid="cases-empty">
          <FolderKanban className="h-10 w-10 mx-auto text-muted-foreground/40" strokeWidth={1.5} />
          <p className="mt-4 text-muted-foreground">Ingen sager endnu.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="pcard p-16 text-center" data-testid="cases-no-match">
          <FolderKanban className="h-10 w-10 mx-auto text-muted-foreground/40" strokeWidth={1.5} />
          <p className="mt-4 text-muted-foreground">Ingen sager matcher de valgte filtre.</p>
          <Button variant="outline" className="mt-5 rounded-xl border-[#EBE8E3]" onClick={resetFilters}>Nulstil filtre</Button>
        </div>
      ) : (
        <div className="pcard overflow-hidden" data-testid="case-list">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#FBFAF8] border-b border-[#EBE8E3]">
            <SortH col="title" span="col-span-3">Sag</SortH>
            <SortH col="responsible" span="col-span-2">Sagsansvarlig</SortH>
            <SortH col="source" span="col-span-2">Kilde</SortH>
            <SortH col="status" span="col-span-2">Status</SortH>
            <SortH col="due" span="col-span-1">Deadline</SortH>
            <SortH col="hours" span="col-span-1" align="right">Timer</SortH>
            <div className="col-span-1 label-caps text-right">Handling</div>
          </div>
          <div className="divide-y divide-[#F0EDE8]">
          {filtered.map((c) => {
            const isOpen = expanded.has(c.id);
            const prog = c.task_total ? Math.round((c.task_done / c.task_total) * 100) : 0;
            return (
              <div key={c.id} data-testid={`case-row-${c.id}`}>
                <div data-testid={`case-row-body-${c.id}`} onClick={() => nav(`/sag/${c.id}`)} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); nav(`/sag/${c.id}`); } }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 md:items-center p-4 md:px-5 md:py-4 cursor-pointer hover:bg-[#FBFAF8] [transition-property:background-color] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A83232]">
                  <div className="md:col-span-3 min-w-0 flex items-center gap-2">
                    <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${PRIO_DOT[c.priority] || PRIO_DOT.Normal}`} title={`Prioritet: ${c.priority}`} />
                    <div className="min-w-0">
                      <p className="font-semibold text-[#1A1919] truncate">{c.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.customer_name || "—"}</p>
                    </div>
                  </div>
                  <div className="md:col-span-2 min-w-0 text-sm text-stone-600 truncate"><span className="md:hidden label-caps mr-2">Ansvarlig</span>{(c.assigned_users || [])[0]?.name || "—"}</div>
                  <div className="md:col-span-2 min-w-0 flex items-center">
                    <span className="md:hidden label-caps mr-2">Kilde</span>
                    {c.source_project_id ? (
                      <span className="inline-flex items-center gap-1.5 min-w-0 text-sm text-stone-600" data-testid={`case-source-project-${c.id}`}>
                        <FolderKanban className="h-3.5 w-3.5 text-[#A83232] shrink-0" strokeWidth={1.75} />
                        <span className="truncate">{c.source_project_name || "Projekt"}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-sm text-stone-500" data-testid={`case-source-new-${c.id}`}>
                        <Building2 className="h-3.5 w-3.5 text-stone-400 shrink-0" strokeWidth={1.75} /> Ny sag
                      </span>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_COLOR[c.status] || STATUS_COLOR["Ny"]}`} data-testid={`case-status-${c.id}`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_DOT[c.status] }} /> {c.status}
                    </span>
                  </div>
                  <div className="md:col-span-1 text-sm text-stone-600" data-testid={`case-end-${c.id}`}><span className="md:hidden label-caps mr-2">Deadline</span>{c.due_date ? daDate(c.due_date) : "Ingen"}</div>
                  <div className="md:col-span-1 text-sm font-semibold text-[#1A1919] md:text-right" data-testid={`case-hours-${c.id}`}><span className="md:hidden label-caps mr-2">Timer</span>{fmtHours(c.total_hours)}</div>
                  <div className="md:col-span-1 flex items-center gap-1 md:justify-end" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={`expand-case-${c.id}`} aria-expanded={isOpen} onClick={() => toggleExpand(c.id)} title="Vis mere" className="p-1.5 rounded-lg text-muted-foreground hover:text-[#1A1919] hover:bg-[#F3F1EC] [transition-property:color,background-color] duration-200"><ChevronDown className={`h-4 w-4 [transition-property:transform] duration-200 ${isOpen ? "rotate-180" : ""}`} /></button>
                    {(c.can_edit && !c.source_project_id)
                      ? <button type="button" data-testid={`case-del-${c.id}`} onClick={() => setCaseToDelete(c)} title="Slet sag" className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-[#F1DEDA] [transition-property:color,background-color] duration-200"><Trash2 className="h-4 w-4" strokeWidth={1.75} /></button>
                      : <span className="p-1.5 text-stone-300" title={c.source_project_id ? "Slettes via projektet" : "Kun din afdeling kan slette"}><Trash2 className="h-4 w-4 opacity-30" /></span>}
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-[#EBE8E3] px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4" data-testid={`case-details-${c.id}`}>
                    <div>
                      <p className="label-caps mb-1.5">Fremdrift</p>
                      <div className="flex items-center gap-2.5"><div className="flex-1 h-1.5 rounded-full bg-[#ECEAE5] overflow-hidden max-w-[120px]"><div className="h-full rounded-full bg-[#A83232]" style={{ width: `${prog}%` }} /></div><span className="text-xs font-semibold text-stone-700">{prog}%</span></div>
                      <p className="text-xs text-muted-foreground mt-1">{c.task_done || 0}/{c.task_total || 0} opgaver</p>
                    </div>
                    <div><p className="label-caps mb-1.5">Startdato</p><p className="text-sm text-stone-600">{c.start_date ? daDate(c.start_date) : "—"}</p></div>
                    <div><p className="label-caps mb-1.5">Oprettet af</p><p className="text-sm text-stone-600">{c.created_by_name || "—"}</p></div>
                    <div className="flex items-end"><button data-testid={`open-case-${c.id}`} onClick={() => nav(`/sag/${c.id}`)} className="inline-flex items-center text-primary font-semibold text-xs">Åbn sag <ChevronRight className="h-4 w-4" /></button></div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="text-xl font-medium">Gem filter</DialogTitle>
            <DialogDescription>Giv dit filter et navn, så du hurtigt kan hente det igen.</DialogDescription></DialogHeader>
          <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Fx: Mine åbne sager" data-testid="case-save-filter-name" className="rounded-xl" onKeyDown={(e) => { if (e.key === "Enter") saveCurrent(); }} />
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setSaveOpen(false)}>Annullér</Button>
            <Button className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={saveCurrent} data-testid="case-save-filter-confirm">Gem</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!caseToDelete} onOpenChange={(o) => !o && setCaseToDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader><AlertDialogTitle className="text-xl font-medium">Slet sag?</AlertDialogTitle>
            <AlertDialogDescription>Sagen "{caseToDelete?.title}" slettes permanent. Dette kan ikke fortrydes.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annullér</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={delCase} data-testid="confirm-delete-case-list">Slet sag</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
